import { errorFromResponse } from "./errors";
import {
  Apps,
  Users,
  Campaigns,
  Uploads,
  Spotify,
  I18n,
  Apple,
  Newsletter,
  Accounts,
  TokenGate,
} from "./modules";

interface RequestOptions {
  includeToken?: boolean;
  params?: { [key: string]: any };
  json?: any;
  body?: any;
  headers?: { [key: string]: string };
  includeSessionId?: boolean;
}

type FGetOriginOrToken = () => string;

export default class CMSClient {
  public apps: Apps;
  public users: Users;
  public campaigns: Campaigns;
  public uploads: Uploads;
  public spotify: Spotify;
  public apple: Apple;
  public i18n: I18n;
  public newsletter: Newsletter;
  public accounts: Accounts;
  public tokenGate: TokenGate;
  public sessionId: string | null = null;
  public host: string | null = null;
  public getOrigin: FGetOriginOrToken;
  public getAccessToken: FGetOriginOrToken;

  constructor(getOrigin: FGetOriginOrToken, getAccessToken: FGetOriginOrToken) {
    this.apps = new Apps(this);
    this.users = new Users(this);
    this.campaigns = new Campaigns(this);
    this.uploads = new Uploads(this);
    this.spotify = new Spotify(this);
    this.apple = new Apple(this);
    this.i18n = new I18n(this);
    this.newsletter = new Newsletter(this);
    this.accounts = new Accounts(this);
    this.tokenGate = new TokenGate(this);
    this.getOrigin = getOrigin;
    this.getAccessToken = getAccessToken;
  }

  private getAuthorizationHeader(): string {
    return this.getAccessToken();
  }

  async request(
    method: string,
    url: string,
    options: RequestOptions = {}
  ): Promise<Response> {
    const urlObj = new URL(url, this.getOrigin());
    const fetchOptions: any = { method: method, headers: {} };
    if (options.json) {
      fetchOptions.body = JSON.stringify(options.json);
      fetchOptions.headers["Content-Type"] = "application/json";
    } else if (options.body) {
      fetchOptions.body = options.body;
    }
    if (options.params) {
      for (const [key, value] of Object.entries(options.params)) {
        urlObj.searchParams.append(key, value);
      }
    }
    if (options.includeToken === true || options.includeToken === undefined) {
      const authHeader = this.getAuthorizationHeader();
      if (authHeader) {
        fetchOptions.headers.Authorization = this.getAuthorizationHeader();
      }
    }
    if (
      options.includeSessionId === true ||
      options.includeSessionId === undefined
    ) {
      if (this.sessionId) {
        fetchOptions.headers["umg-cms-session-id"] = this.sessionId;
      }
    }
    fetchOptions.headers = {
      ...fetchOptions.headers,
      ...(options.headers || {}),
    };

    const resp = await fetch(urlObj.toString(), fetchOptions);
    return await errorFromResponse(resp);
  }

  async get(url: string, options: RequestOptions = {}): Promise<Response> {
    return await this.request("GET", url, options);
  }

  async post(url: string, options: RequestOptions = {}): Promise<Response> {
    return await this.request("POST", url, options);
  }

  async put(url: string, options: RequestOptions = {}): Promise<Response> {
    return await this.request("PUT", url, options);
  }

  async delete(url: string, options: RequestOptions = {}): Promise<Response> {
    return await this.request("DELETE", url, options);
  }

  async patch(url: string, options: RequestOptions = {}): Promise<Response> {
    return await this.request("PATCH", url, options);
  }
}
